import { memo, useCallback, useEffect, useState } from 'react'
import { Trash2, Wand2 } from 'lucide-react'
import { playoutApi, type AutoGapFillRun } from '../../lib/api.js'
import { Spinner } from '../common/Spinner.js'

/**
 * Rappel des comblages automatiques posés sur la journée affichée.
 *
 * Il existe pour une seule raison : la pile d'annulation du poste meurt avec
 * l'onglet, alors qu'un comblage pose deux cents éléments d'un clic. Tant qu'une
 * passe n'a pas été retirée et qu'il lui reste des éléments en grille, elle
 * reste donc retirable ici — y compris le lendemain, depuis un autre poste.
 *
 * Une passe dont tout est passé à l'antenne disparaît du bandeau : il n'y a plus
 * rien à retirer, et proposer le geste laisserait croire le contraire.
 */

interface GapFillRunsBannerProps {
  channelId: string
  /** Journée d'antenne affichée, `AAAA-MM-JJ`. */
  date:      string
  /** Change à chaque écriture de grille : relit les passes. */
  refreshKey: number
  onReverted: (message: string) => void
}

function fmtCreatedAt(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

export const GapFillRunsBanner = memo(function GapFillRunsBanner({
  channelId,
  date,
  refreshKey,
  onReverted,
}: GapFillRunsBannerProps) {
  const [runs, setRuns] = useState<AutoGapFillRun[]>([])
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    playoutApi.listAutoGapFillRuns(channelId, date)
      .then(result => { if (!cancelled) setRuns(result) })
      .catch(() => { if (!cancelled) setRuns([]) })
    return () => { cancelled = true }
  }, [channelId, date, refreshKey])

  const handleRevert = useCallback(async (run: AutoGapFillRun) => {
    const frozen = run.frozenCount > 0
      ? ` ${run.frozenCount} élément(s) déjà à l’antenne seront conservés.`
      : ''
    if (!window.confirm(`Retirer les ${run.remainingCount} élément(s) posés par ce comblage ?${frozen}`)) return

    setBusyId(run.id)
    try {
      const result = await playoutApi.revertAutoGapFillRun(channelId, run.id)
      const kept = result.keptCount > 0
        ? ` ${result.keptCount} élément(s) déjà à l’antenne conservé(s).`
        : ''
      onReverted(`Comblage retiré : ${result.removedCount} élément(s) supprimé(s).${kept}`)
    } catch (exception: any) {
      window.alert(exception?.message ?? 'Échec du retrait du comblage')
    } finally {
      setBusyId(null)
    }
  }, [channelId, onReverted])

  const pending = runs.filter(run => !run.revertedAt && run.remainingCount > 0)
  if (pending.length === 0) return null

  return (
    <div className="agenda-runs-banner" role="status">
      {pending.map(run => (
        <div key={run.id} className="agenda-runs-banner__row">
          <Wand2 size={14} aria-hidden="true" className="agenda-runs-banner__icon" />
          <span className="agenda-runs-banner__text">
            <strong>Comblage de {fmtCreatedAt(run.createdAt)}</strong>
            {' — '}
            {run.gapCount} trou{run.gapCount > 1 ? 's' : ''} · {run.remainingCount} élément{run.remainingCount > 1 ? 's' : ''} retirable{run.remainingCount > 1 ? 's' : ''}
            {run.frozenCount > 0 && ` · ${run.frozenCount} déjà à l’antenne`}
          </span>
          <button
            type="button"
            className="tp-button tp-button--danger-outline"
            onClick={() => { handleRevert(run).catch(console.error) }}
            disabled={busyId !== null}
          >
            {busyId === run.id ? <Spinner /> : <Trash2 size={13} aria-hidden="true" />}
            Retirer les ajouts
          </button>
        </div>
      ))}
    </div>
  )
})
